import { Images } from 'assets/images/images.index';
import {
  ButtonAddItem,
  ErrorAlert,
  GenericSectionError,
  ItemMenuList,
  ItemSubscription,
  LoadingSkeleton,
  Modal,
  PopUpInformative,
  SuccessAlert,
  TitleSection,
} from 'components';
import { TypeState } from 'models/app.interface';
import { useEffect, useState } from 'react';
import { useSubscriptions } from '../hooks/Subscriptions.hook';
import { useSubscriptionAlerts } from '../hooks/useSubscriptionAlerts';
import { useSelector } from 'react-redux';
import { IAppReducer } from 'models';
import { useNavigate } from 'react-router';
import {
  DataStoreFlags,
  getDataStoreFlags,
} from 'shared/services/flags.service';
import { renewalSingleSubscriptionService } from 'shared/services/payments';
import { Subscription } from 'shared/services/user-subscription';

const DAYS_RANGE_PLAN_CANCELLED = 14;

const getTimeRangePlanCancelled = (
  dateCancel: string | undefined,
  status: Subscription['status']
): boolean => {
  if (status === 'pending') return true;
  if (!dateCancel) return false;
  const oneDayMs = 24 * 60 * 60 * 1000;
  const rangeMs = DAYS_RANGE_PLAN_CANCELLED * oneDayMs;
  const now = Date.now();
  const cancelTime = new Date(dateCancel).getTime();
  const diff = now - cancelTime;
  return diff >= 0 && diff <= rangeMs;
};

const canRenewSubscription = (
  sub: Subscription,
  userImpersonationId: string | number | null,
  branch: string
): boolean => {
  if (branch?.toLowerCase() !== 'pr') return false;
  if (userImpersonationId == null) return false;
  if (['successful'].includes(sub.status)) return false;
  if (sub.isFreemium === true) return false;
  return getTimeRangePlanCancelled(sub.dateCancel, sub.status);
};

interface RenewalModalState {
  isOpen: boolean;
  loading: boolean;
  type: TypeState;
  title: string;
  message: string;
}

const initialRenewalModalState: RenewalModalState = {
  isOpen: false,
  loading: false,
  type: 'warning',
  title: 'Renovando plan',
  message: 'Esto tomará un momento',
};

export const SubscriptionsList = () => {
  const { subscriptions, error, loading } = useSubscriptions();
  const [showAddModal, setShowAddModal] = useState(false);
  const [flags, setFlags] = useState<DataStoreFlags | null>(null);
  const [isLoadingRenewalSubscription, setIsLoadingRenewalSubscription] =
    useState<RenewalModalState>(initialRenewalModalState);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    getDataStoreFlags().then((data) => {
      if (!cancelled) setFlags(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const {
    successAlert,
    errorAlert,
    showSuccessAlert,
    showErrorAlert,
    hideSuccessAlert,
    hideErrorAlert,
  } = useSubscriptionAlerts();

  const subsActive = subscriptions.filter((sub) => sub.status === 'successful');

  const { authReducer: user, branchReducer } = useSelector(
    (state: IAppReducer) => state
  );
  const userImpersonationId = user?.agentAuthData?.userId ?? null;
  const uid = user?.uid ?? null;
  const branch = branchReducer?.branch ?? '';

  const isTester = user?.agentAuthData?.isTester ?? false;

  const handleSuccess = () => {
    showSuccessAlert('La operación se completó exitosamente');
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  const handleError = (errorMessage: string) => {
    showErrorAlert(errorMessage);
  };

  const handleRenewalSubscription = async (subscriptionId: string) => {
    setIsLoadingRenewalSubscription({
      isOpen: true,
      loading: true,
      type: 'warning',
      title: 'Renovando suscripción',
      message: 'Esto puede tardar unos minutos',
    });
    try {
      await renewalSingleSubscriptionService(
        subscriptionId,
        userImpersonationId != null ? Number(userImpersonationId) : null
      );
      handleSuccess();
    } catch {
      handleError('Ocurrió un error, inténtalo más tarde');
    } finally {
      setIsLoadingRenewalSubscription({
        isOpen: false,
        loading: false,
        type: 'warning',
        title: 'Renovando suscripción',
        message: 'Esto puede tardar unos minutos',
      });
    }
  };

  const closeRenewalModal = () => {
    setIsLoadingRenewalSubscription(initialRenewalModalState);
  };

  if (!uid || !userImpersonationId) {
    return <h1>No tienes permisos para ver esta página</h1>;
  }

  return (
    <>
      <SuccessAlert
        isOpen={successAlert.show}
        title={successAlert.title}
        message={successAlert.message}
        onClose={hideSuccessAlert}
      />
      <ErrorAlert
        isOpen={errorAlert.show}
        title={errorAlert.title}
        message={errorAlert.message}
        onClose={hideErrorAlert}
      />
      {isLoadingRenewalSubscription.isOpen && (
        <PopUpInformative
          title={isLoadingRenewalSubscription.title}
          type={isLoadingRenewalSubscription.type}
          closePopUp={closeRenewalModal}
        >
          {isLoadingRenewalSubscription.message}
          {isLoadingRenewalSubscription.loading ? (
            <div className="payments__loader__container">
              <div className="payments__loader__content">
                <div className="payments__loader" />
              </div>
            </div>
          ) : null}
        </PopUpInformative>
      )}
      <TitleSection title="suscripciones">
        <ButtonAddItem
          testid="btn_subscriptions_new_subscription"
          label="Agregar"
          onClick={() => {
            if (isTester && flags?.insurance) {
              setShowAddModal(true);
            } else {
              navigate('/settings/subscriptions/add-subscription');
            }
          }}
          template="primary"
        />
      </TitleSection>
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="¿Qué deseas agregar?"
        size="small"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <ItemMenuList
            id="btn_add_subscription"
            label="Crear suscripción"
            subTitle="Agrega una nueva suscripción con productos"
            link="/settings/subscriptions/add-subscription"
            icon={Images.properties}
          />
          <ItemMenuList
            id="btn_add_insurance"
            label="Crear seguro"
            subTitle="Agrega un nuevo seguro para tu propiedad"
            link="/settings/subscriptions/create-insurance"
            icon={Images.icono_plan}
          />
        </div>
      </Modal>
      <div className="subscriptions__content">
        <div className="subscriptions__content__items">
          {error ? (
            <GenericSectionError template="tertiary" />
          ) : loading ? (
            <LoadingSkeleton count={2} />
          ) : !subscriptions?.length ? (
            <span>No tienes suscripciones</span>
          ) : (
            subscriptions.map((sub) => (
              <ItemSubscription
                key={sub.id}
                id={sub.id}
                dbId={sub.dbId}
                version={sub.version}
                products={sub.products}
                price={sub.price}
                dateInit={sub.dateInit}
                dateInitRaw={sub.dateInitRaw}
                dateExpire={sub.dateExpire}
                dateExpireRaw={sub.dateExpireRaw}
                hasTrial={sub.hasTrial}
                typePeriodicity={sub.typePeriodicity}
                status={sub.status}
                providerProcessor={sub.providerProcessor}
                uid={uid}
                userImpersonationId={userImpersonationId?.toString()}
                onSuccess={handleSuccess}
                onError={handleError}
                onRenewal={
                  canRenewSubscription(sub, userImpersonationId, branch)
                    ? handleRenewalSubscription
                    : undefined
                }
                totalSubsActive={subsActive?.length ?? 0}
                productsDetails={sub.productsDetails}
                payments={sub.payments}
                wasMigrated={sub.wasMigrated}
                calculateAmount={sub.calculateAmount}
                company={sub.company}
                isInsurance={sub.isInsurance}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
};
